<script>
	const Vue = require('vue');
	const pubsub = require('events/pubsub');
	const { nextStep, prevStep, dismissJourney } = require('utilities/journeyUtilities');
	const {
		fireEvent,
		events: { JOURNEY_ACTION }
	} = require('utilities/analyticsUtilities');

	module.exports = {
		components: {
			'click-elsewhere': require('components/click-elsewhere.vue').default,
			'x-icon': require('components/svg/x-icon.vue').default,
			'journey-icon-container': require('components/journey-icon-container.vue').default,
			'outbound-link': require('components/outbound-link.vue').default
		},
		modelData: ['dismissedJourneys'],
		data() {
			return {
				showTooltip: false,
				targetEl: null,
				journeyKey: '',
				icon: {},
				coords: null,
				title: '',
				text: '',
				links: [],
				next: undefined,
				hideNext: false,
				prev: undefined,
				hidePrev: false,
				dismiss: undefined,
				position: 'b',
				startTimestamp: 0,
				currentStep: undefined,
				totalSteps: undefined
			};
		},
		created() {
			pubsub.subscribe('journey-dialog-update', this.onTooltipChange);
			this.next = nextStep;
			this.prev = prevStep;
		},
		computed: {
			style() {
				const coords = this.coords;
				const position = this.position;
				const el = this.$el;

				if (!coords || !position || !el) {
					return {};
				}

				const { width: dialogWidth, height: dialogHeight } = el.getBoundingClientRect();
				const caretDimensions = 17;
				const scrollTop = window.scrollY;

				// mid point plus padding for caret
				const y = coords.top + coords.height / 2;

				// mid point subtract half the width of the tooltip
				const x = coords.left + coords.width / 2;

				const verticalCentering = Math.min(Math.max(y - dialogHeight / 2 + scrollTop, 0), window.innerHeight);
				const horizontalCentering = Math.min(Math.max(x - dialogWidth / 2, 0), window.innerWidth);

				const positions = {
					l: {
						top: verticalCentering,
						left: x - coords.width / 2 - dialogWidth - caretDimensions
					},
					r: {
						top: verticalCentering,
						left: x + coords.width / 2 + caretDimensions
					},
					t: {
						top: y - coords.height / 2 - dialogHeight + scrollTop,
						left: horizontalCentering
					},
					b: {
						top: y + coords.height / 2 + caretDimensions + scrollTop,
						left: horizontalCentering
					},
					tl: {
						top: coords.top,
						left: x - coords.width / 2 - dialogWidth - caretDimensions
					}
				};

				const { top, left } = positions[position] || {
					top: 0,
					left: 0
				};

				return {
					top: top + 'px',
					left: left + 'px'
				};
			},
			positionClass() {
				const positionMap = {
					l: 'dialog-left',
					r: 'dialog-right',
					t: 'dialog-top',
					b: 'dialog-bottom',
					tl: 'dialog-top-left'
				};
				const position = this.position;

				return positionMap[position];
			},
			ariaLabel() {
				return `${this.journeyTitle} Step ${this.currentStep} of ${this.totalSteps}`;
			}
		},
		methods: {
			focusEl(el = this.$refs.continueButton) {
				Vue.nextTick(() => {
					el.focus();
				});
			},
			onTooltipChange(data) {
				if (!data || (data && !data.el && data.journeyKey === this.journeyKey)) {
					this.clear();
					return;
				}

				const dismissedJourneys = this.dismissedJourneys || {};

				if (data && !dismissedJourneys[data.journeyKey]) {
					const {
						text,
						el,
						position,
						journeyKey,
						journeyTitle,
						icon,
						coordinates,
						title,
						links,
						hidePrev,
						hideNext,
						startTimestamp,
						currentStep,
						totalSteps
					} = data;

					if (startTimestamp < this.startTimestamp) {
						return;
					}

					if (el && text) {
						this.journeyKey = journeyKey;
						this.journeyTitle = journeyTitle;
						this.targetEl = el;
						this.icon = icon;
						this.title = title;
						this.text = text;
						this.links = links;
						this.hideNext = hideNext;
						this.hidePrev = hidePrev;
						this.startTimestamp = startTimestamp;
						this.currentStep = currentStep;
						this.totalSteps = totalSteps;
						this.showTooltip = true;

						Vue.nextTick(() => {
							this.position = position;
							this.coords = Object.assign({}, el.getBoundingClientRect().toJSON(), coordinates);

							if (!this.isInViewHorizontally(el)) {
								el.scrollIntoView({
									block: 'nearest',
									inline: 'nearest'
								});
							}

							this.focusEl();
						});
					}
				}
			},
			clear() {
				if (!this.journeyKey) {
					return;
				}
				this.targetEl = null;
				this.journeyKey = '';
				this.journeyTitle = '';
				this.coords = null;
				this.icon = {};
				this.title = '';
				this.text = '';
				this.links = [];
				this.hideNext = false;
				this.hidePrev = false;
				this.position = 'b';
				this.startTimestamp = 0;
				this.currentStep = undefined;
				this.totalSteps = undefined;
				this.showTooltip = false;
			},
			close() {
				const journeyKey = this.journeyKey;
				dismissJourney(journeyKey);
				fireEvent(JOURNEY_ACTION, {
					name: journeyKey,
					type: 'dismiss',
					idx: this.currentStep - 1
				});
				this.focusEl(this.targetEl);
				this.next(journeyKey);
				this.clear();
			},
			isInViewHorizontally(element) {
				const { right, left } = element.getBoundingClientRect();

				return left >= 0 && right <= window.innerWidth;
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';

	.journey-dialog {
		top: -100%;
		left: -100%;

		.journey-dialog-link:not(.inline) {
			margin-top: 0.25rem;
			display: block;
			font-weight: 700;
		}

		.training-navigation {
			flex: 1 0 100%;
			display: flex;
			justify-content: flex-end;
			margin-top: 0.5rem;
		}

		.previous-step {
			border: none;

			&:hover {
				border: none;
			}
		}

		.next-step {
			margin-left: 0.5rem;
		}
	}
</style>
<template>
	<click-elsewhere
		class="journey-dialog help-dialog"
		:class="{ [positionClass]: true, visible: showTooltip }"
		:style="style"
		role="dialog"
		:aria-label="ariaLabel"
		v-show="showTooltip"
		@clicked-elsewhere="close"
		:exceptions="[targetEl]"
		@keydown.native.esc="close">
		<button
			class="close-button button -link"
			ref="closeButton"
			@click="close"
			aria-label="Dismiss this step">
			<x-icon></x-icon>
		</button>
		<journey-icon-container
			class="help-dialog-icon-container"
			:icon="icon.name"
			:color="icon.color"></journey-icon-container>
		<div
			class="journey-dialog-body help-dialog-body"
			aria-live="polite">
			<div
				v-if="title"
				class="journey-dialog-title help-dialog-title">
				{{ title }}
			</div>
			<span
				v-if="text"
				class="journey-dialog-text"
				>{{ text }}</span
			>
			<span
				v-for="(link, index) in links"
				:key="link.url"
				class="journey-dialog-link"
				:class="{ inline: link.inline || index < links.length - 1 }">
				<span v-if="link.precedingText">{{ link.precedingText }}</span>
				<outbound-link
					v-if="link.outbound"
					:href="link.url"
					:data-outbound-link="`JOURNEY_${journeyKey}_STEP_${currentStep}`"
					>{{ link.text }}</outbound-link
				>
				<a
					v-else
					:href="link.url"
					target="_blank"
					>{{ link.text }} </a
				><span v-if="link.followingText">{{ link.followingText }} </span>
			</span>
		</div>
		<div class="training-navigation">
			<button
				v-if="!(hidePrev || currentStep === 1)"
				class="button -reverse previous-step"
				@click="prev(journeyKey, true)">
				<i
					class="fa-solid fa-chevron-left"
					aria-hidden="true"></i>
				Previous
			</button>
			<button
				v-if="!hideNext"
				ref="continueButton"
				v-text="currentStep === totalSteps ? 'Done' : 'Next'"
				class="button next-step"
				@click="next(journeyKey, true)"></button>
		</div>
		<button
			@focus="focusEl($els.closeButton || $els.continueButton)"
			class="focus-capture hidden-visually"
			aria-hidden="true"></button>
	</click-elsewhere>
</template>
